// Module frontend surface.
//
// The shell discovers this file automatically (modules/*/frontend/index.js) and
// merges what it exports. Adding this module needs no edit to the shell.
//
// Two navigation entries and that is deliberate. «Mi vehículo» is not a child
// of Flota: they are different screens for different people, and for a driver
// «Mi vehículo» is the whole module. The shell filters both by permission
// before drawing them, so the office sees one and the yard sees the other.

export const routes = [
  {
    path: '/my-vehicle',
    name: 'my-vehicle',
    component: () => import('./pages/MyVehiclePage.vue'),
    meta: { permission: 'fleet:drive', title: 'fleet.myVehicle.title' },
  },
  {
    path: '/fleet',
    name: 'fleet',
    component: () => import('./pages/FleetPage.vue'),
    meta: { permission: 'fleet:read', title: 'fleet.title' },
  },
  {
    path: '/fleet/new',
    name: 'vehicle-new',
    component: () => import('./pages/VehicleFormPage.vue'),
    meta: { permission: 'fleet:manage', title: 'fleet.newTitle' },
  },
  {
    path: '/fleet/:id',
    name: 'vehicle',
    component: () => import('./pages/VehiclePage.vue'),
    meta: { permission: 'fleet:read', title: 'fleet.detailTitle' },
  },
  // La ficha se lee en /fleet/:id y se escribe aqui, como en el resto del ERP.
  // Antes las dos cosas eran la misma pantalla: quien entraba a mirar cuantos
  // kilometros lleva una furgoneta se encontraba diez campos de formulario.
  {
    path: '/fleet/:id/edit',
    name: 'vehicle-edit',
    component: () => import('./pages/VehicleFormPage.vue'),
    meta: { permission: 'fleet:manage', title: 'fleet.form.editTitle' },
  },
]

export const navigation = [
  // Coger la furgoneta, repostar y soltarla: tres cosas que se hacen en un
  // aparcamiento, ninguna sentado.
  { to: '/my-vehicle', labelKey: 'fleet.myVehicle.title', permission: 'fleet:drive', icon: 'key', group: 'business', order: 40 },
  { to: '/fleet', labelKey: 'fleet.title', permission: 'fleet:read', icon: 'van', group: 'business' },
]

// Strings this module contributes, merged per locale. Four languages from the
// first commit (§16): a module that ships two is a module that cannot be sold
// to the third client.
const baseMessages = {
  es: {
    errors: {
      ALREADY_DRIVING: 'Ya llevas {code}. Devuélvelo antes de coger otro.',
      ALREADY_VOID: 'Ese repostaje no existe o ya está anulado.',
      NOT_YOUR_VEHICLE: 'Ese vehículo no lo llevas tú: no puedes devolverlo.',
      PLATE_TAKEN: 'Esa matrícula ya está en {code}.',
      VEHICLE_FREE: 'Ese vehículo no lo lleva nadie.',
      VEHICLE_IN_USE: 'Ese vehículo está fuera con alguien. Primero tiene que devolverse.',
      VEHICLE_NOT_AVAILABLE: 'Ese vehículo no está disponible.',
      VEHICLE_TAKEN: 'Ese vehículo lo lleva otra persona: {driver}.',
    },
    fleet: {
      // Los avisos de la mañana y de la tarjeta «Hoy» (backend/nudges.js).
      nudges: {
        serviceDue: '{count} vehículos pasados de revisión',
        serviceDueOne: 'revisión de {vehicle}',
      },
      // La respuesta que contestan las reglas sin el modelo (queries.js).
      assistant: {
        query: {
          mine: {
            current: 'Llevas {vehicle}.',
            assigned: 'Tienes asignado: {list}.',
            none: 'No llevas ningún vehículo; hay {free} libres.',
            noneFree: 'No llevas ningún vehículo y no hay ninguno libre.',
          },
        },
        // La tarjeta de «he repostado 40 litros, 72 €» (assistant.js, intents.js).
        fuel: {
          summary: {
            plain: 'Repostaje de {litres} l en {vehicle}',
            paid: 'Repostaje de {litres} l, {total} €, en {vehicle}',
          },
          action: 'Apuntar repostaje',
          field: {
            vehicle: 'Vehículo',
            litres: 'Litros',
            total: 'Importe (€)',
            price: 'Precio por litro',
            km: 'Cuentakilómetros (km)',
            station: 'Gasolinera',
          },
          where: 'Vehículo',
          note: {
            noVehicle: 'Elige el vehículo: no queda claro por lo dicho',
            noKm: 'Falta lo que marca el cuentakilómetros',
            belowKm: 'El cuentakilómetros no va hacia atrás: estaba en {km} km',
            photo: 'Esta empresa exige la foto del ticket: apúntalo desde Mi vehículo',
          },
          spoken: { plain: '{litres} litros. ¿Lo apunto?', paid: '{litres} litros, {total} euros. ¿Lo apunto?' },
          askLitres: '¿Cuántos litros?',
          askKm: '¿Qué marca el cuentakilómetros?',
          outcome: 'Repostaje {code} apuntado',
        },
      },
      title: 'Flota',
      dashboard: {
        available: 'Vehículos libres',
        availableHelp: 'Cuántos vehículos activos no los lleva nadie ahora mismo.',
        out: 'Vehículos fuera',
        outHelp: 'Cuántos vehículos están cogidos por alguien en este momento.',
        inWorkshop: 'En taller',
        inWorkshopHelp: 'Cuántos vehículos están marcados como en mantenimiento y no se pueden coger.',
        fuelMonth: 'Combustible del mes',
        fuelMonthHelp: 'Lo gastado en repostajes desde el día 1 del mes en curso, en euros.',
        latestRefuels: 'Últimos repostajes',
        latestRefuelsHelp: 'Los repostajes más recientes, con la matrícula y los litros. Estira la tarjeta y caben más.',
      },
      subtitle: 'Los vehículos de la empresa, quién los lleva y qué gastan',
      detailTitle: 'Vehículo',
      newTitle: 'Vehículo nuevo',
      newHint: 'Un nombre, una matrícula y los kilómetros que marca hoy',
      empty: 'Todavía no hay ningún vehículo',
      noRefuels: 'Todavía no hay repostajes',
      saved: 'Vehículo guardado',
      archived: 'Vehículo archivado',
      restored: 'Vehículo devuelto a la flota',
      archivedBanner: 'Este vehículo está archivado: no sale en ninguna lista y nadie puede cogerlo.',
      tookIt: 'Has cogido {name}',
      outWith: 'Ahora mismo la lleva {name}.',
      consumptionHint: 'El consumo sale a partir del segundo repostaje: hace falta una distancia entre dos para poder calcularlo.',
      voidedFromScreen: 'Anulado desde la ficha del vehículo',
      myVehicle: {
        title: 'Mi vehículo',
        subtitle: 'Qué llevas ahora y qué puedes coger',
        yours: 'La llevas tú',
        none: 'No llevas ningún vehículo',
        noneFree: 'Y ahora mismo no queda ninguno libre. Pregunta en oficina.',
        free: 'Libres ahora',
        freeHint: 'Coge uno y queda a tu nombre hasta que lo sueltes',
        assigned: 'Asignados a ti',
        assignedHint: 'Los que la oficina te tiene puestos de forma fija',
        myRefuels: 'Mis repostajes',
      },
      actions: {
        new: 'Nuevo vehículo',
        refuel: 'Apuntar repostaje',
        release: 'Soltar',
        pickUp: 'Coger',
        service: 'Apuntar revisión',
        archive: 'Archivar',
        restore: 'Devolver a la flota',
        forceRelease: 'Soltarlo de todas formas',
        void: 'Anular',
        edit: 'Editar',
        ticket: 'Ver tiquet',
      },
      sections: {
        identity: 'El vehículo',
        out: 'Está fuera',
        refuels: 'Repostajes',
        tickets: 'Tiquets',
      },
      ticketsHint: 'Las fotos que se hicieron en el surtidor, para cuadrar la tarjeta de combustible',
      noTickets: 'Todavía no hay ningún tiquet. Aparecen aquí en cuanto alguien apunte un repostaje con foto.',
      ticketOf: 'Tiquet del {date}',
      moreTickets: 'Hay {count} tiquets más. Se abren desde su fila de la tabla de abajo.',
      form: {
        editTitle: 'Editar vehículo',
        backToSheet: 'La ficha',
        changes: 'Lo que cambia',
        changesHint: 'Lo que se toca cada semana. Lo demás está más abajo.',
        identityHint: 'Se escribe una vez y no se vuelve a mirar.',
      },
      fields: {
        vehicle: 'Vehículo',
        name: 'Nombre',
        plate: 'Matrícula',
        brand: 'Marca',
        model: 'Modelo',
        year: 'Año',
        fuelType: 'Combustible',
        status: 'Estado',
        statusHint: 'Un vehículo que alguien lleva no se puede mandar al taller desde aquí',
        km: 'Kilómetros',
        kmHint: 'Solo suben. Un repostaje los actualiza solo',
        since: 'Cogido',
        who: 'Quién',
        date: 'Fecha',
        litres: 'Litros',
        totalCost: 'Importe',
        station: 'Gasolinera',
        photo: 'Foto del ticket',
        notes: 'Notas',
        note: 'Nota',
        assignedTo: 'Asignado a',
        assignedToHint: 'La asignación fija, distinta de quién lo lleva hoy',
        nobody: 'Nadie',
      },
      statuses: { active: 'Activo', maintenance: 'En taller', inactive: 'De baja' },
      states: { free: 'Libre', out: 'Fuera' },
      fuels: {
        diesel: 'Diésel', petrol: 'Gasolina', electric: 'Eléctrico',
        hybrid: 'Híbrido', lpg: 'GLP', other: 'Otro',
      },
      stats: {
        total: 'Vehículos',
        available: 'Libres',
        out: 'Fuera',
        maintenance: 'En taller',
        consumption: 'Consumo / 100 km',
        refuels: 'Repostajes',
        sinceService: 'Km desde la revisión',
      },
      filters: {
        search: 'Buscar',
        searchPlaceholder: 'Matrícula, nombre, marca…',
        allStatuses: 'Todos',
        availability: 'Disponibilidad',
        allAvailability: 'Todos',
        show: 'Ver',
        inFleet: 'En la flota',
        archived: 'Archivados',
      },
      refuel: {
        title: 'Apuntar un repostaje',
        kmHint: 'Los que marca el cuadro ahora mismo',
        costHint: 'El total del ticket. El precio por litro sale solo',
        photoHint: 'Opcional, salvo que la empresa la exija',
        saved: 'Repostaje apuntado',
      },
      release: {
        title: 'Soltar el vehículo',
        kmHint: 'Si te has fijado. Si no, déjalo en blanco',
        done: 'Vehículo soltado',
      },
      service: {
        title: 'Apuntar una revisión',
        kmHint: 'Los que marcaba al pasar por el taller',
        saved: 'Revisión apuntada',
      },
      confirmArchive: {
        title: '¿Archivar el vehículo?',
        message: '«{name}» dejará de aparecer. Sus repostajes y el trabajo que lo nombra siguen ahí.',
      },
      confirmForce: {
        title: '¿Soltarlo de todas formas?',
        message: 'Lo tiene {name}. Suéltalo solo si sabes que el vehículo ya está de vuelta.',
      },
      confirmVoid: {
        title: '¿Anular el repostaje?',
        message: '{code} dejará de contar para el consumo, pero se queda en la lista tachado.',
      },
      notifications: {
        categories: {
          serviceDue: 'Revisión de un vehículo',
          released: 'Te sueltan un vehículo',
        },
        released: {
          title: 'Ya no llevas {vehicle}',
          body: '{who} ha devuelto {plate} a la flota.',
        },
        serviceDue: {
          title: 'Hay vehículos para revisión',
          body: '{count} vehículo(s) han pasado el intervalo. El primero, {first}.',
        },
      },
    },
    modules: { fleet: 'Flota' },
    permissions: {
      fleet: {
        read: 'Ver la flota y quién lleva cada vehículo',
        drive: 'Coger un vehículo, soltarlo y apuntar repostajes',
        manage: 'Dar de alta vehículos y ver el gasto de toda la flota',
      },
    },
    audit: {
      actions: {
        vehicle: {
          created: 'Vehículo dado de alta',
          updated: 'Vehículo modificado',
          serviced: 'Revisión apuntada',
          archived: 'Vehículo archivado',
          restored: 'Vehículo devuelto a la flota',
          picked_up: 'Alguien coge un vehículo',
          released: 'Alguien suelta un vehículo',
          force_released: 'Se libera un vehículo ajeno',
        },
        fuel_log: {
          recorded: 'Repostaje apuntado',
          voided: 'Repostaje anulado',
        },
      },
    },
    assistant: {
      openers: {
        fleet: {
          'what-am-i-driving': '¿Qué vehículo llevo ahora?',
          'log-refuel': 'Acabo de repostar',
          'any-free': '¿Queda alguna furgoneta libre?',
          'fuel-spend': '¿Cuánto llevamos gastado en combustible?',
        },
      },
    },
    settings: {
      ns: {
        fleet: {
          title: 'Flota',
          subtitle: 'Cómo se cogen los vehículos y qué se exige al repostar',
          oneVehiclePerDriver: {
            label: 'Una persona solo puede llevar un vehículo a la vez',
            help: 'Encendido, porque es lo que hace que «quién tiene la Kangoo» tenga una sola respuesta.',
          },
          requireTicketPhoto: {
            label: 'Exigir la foto del ticket en cada repostaje',
            help: 'Apagado de serie: un surtidor sin impresora o un móvil sin batería acaban en litros sin apuntar.',
          },
          serviceEveryKm: {
            label: 'Avisar cuando un vehículo pase estos kilómetros desde su revisión',
            help: '0 apaga el aviso. Se mide desde la última revisión apuntada, no desde cero.',
          },
          serviceAlertRoles: {
            label: 'Quién se entera de que toca revisión',
            help: 'Se comprueba una vez cada noche.',
          },
        },
      },
    },
  },

  ca: {
    errors: {
      ALREADY_DRIVING: 'Ja portes {code}. Torna’l abans d’agafar-ne un altre.',
      ALREADY_VOID: 'Aquest repostatge no existeix o ja està anul·lat.',
      NOT_YOUR_VEHICLE: 'Aquest vehicle no el portes tu: no el pots tornar.',
      PLATE_TAKEN: 'Aquesta matrícula ja és a {code}.',
      VEHICLE_FREE: 'Aquest vehicle no el porta ningú.',
      VEHICLE_IN_USE: 'Aquest vehicle és fora amb algú. Primer s’ha de tornar.',
      VEHICLE_NOT_AVAILABLE: 'Aquest vehicle no està disponible.',
      VEHICLE_TAKEN: 'Aquest vehicle el porta una altra persona: {driver}.',
    },
    fleet: {
      // Los avisos de la mañana y de la tarjeta «Hoy» (backend/nudges.js).
      nudges: {
        serviceDue: '{count} vehicles passats de revisió',
        serviceDueOne: 'revisió de {vehicle}',
      },
      // La respuesta que contestan las reglas sin el modelo (queries.js).
      assistant: {
        query: {
          mine: {
            current: 'Portes {vehicle}.',
            assigned: 'Tens assignat: {list}.',
            none: 'No portes cap vehicle; n’hi ha {free} de lliures.',
            noneFree: 'No portes cap vehicle i no n’hi ha cap de lliure.',
          },
        },
        fuel: {
          summary: {
            plain: 'Repostatge de {litres} l a {vehicle}',
            paid: 'Repostatge de {litres} l, {total} €, a {vehicle}',
          },
          action: 'Apuntar repostatge',
          field: {
            vehicle: 'Vehicle',
            litres: 'Litres',
            total: 'Import (€)',
            price: 'Preu per litre',
            km: 'Comptaquilòmetres (km)',
            station: 'Benzinera',
          },
          where: 'Vehicle',
          note: {
            noVehicle: 'Tria el vehicle: no queda clar pel que s’ha dit',
            noKm: 'Falta el que marca el comptaquilòmetres',
            belowKm: 'El comptaquilòmetres no va enrere: era a {km} km',
            photo: 'Aquesta empresa exigeix la foto del tiquet: apunta’l des de El meu vehicle',
          },
          spoken: { plain: '{litres} litres. Ho apunto?', paid: '{litres} litres, {total} euros. Ho apunto?' },
          askLitres: 'Quants litres?',
          askKm: 'Què marca el comptaquilòmetres?',
          outcome: 'Repostatge {code} apuntat',
        },
      },
      title: 'Flota',
      dashboard: {
        available: 'Vehicles lliures',
        availableHelp: 'Quants vehicles actius no els porta ningú ara mateix.',
        out: 'Vehicles fora',
        outHelp: 'Quants vehicles estan agafats per algú en aquest moment.',
        inWorkshop: 'Al taller',
        inWorkshopHelp: 'Quants vehicles estan marcats com en manteniment i no es poden agafar.',
        fuelMonth: 'Combustible del mes',
        fuelMonthHelp: 'El que s\'ha gastat en repostatges des del dia 1 del mes en curs, en euros.',
        latestRefuels: 'Últims repostatges',
        latestRefuelsHelp: 'Els repostatges més recents, amb la matrícula i els litres. Estira la targeta i n\'hi caben més.',
      },
      subtitle: 'Els vehicles de l\'empresa, qui els porta i què gasten',
      detailTitle: 'Vehicle',
      newTitle: 'Vehicle nou',
      newHint: 'Un nom, una matrícula i els quilòmetres que marca avui',
      empty: 'Encara no hi ha cap vehicle',
      noRefuels: 'Encara no hi ha repostatges',
      saved: 'Vehicle desat',
      archived: 'Vehicle arxivat',
      restored: 'Vehicle retornat a la flota',
      archivedBanner: 'Aquest vehicle està arxivat: no surt a cap llista i ningú el pot agafar.',
      tookIt: 'Has agafat {name}',
      outWith: 'Ara mateix el porta {name}.',
      consumptionHint: 'El consum surt a partir del segon repostatge: cal una distància entre dos per poder calcular-lo.',
      voidedFromScreen: 'Anul·lat des de la fitxa del vehicle',
      myVehicle: {
        title: 'El meu vehicle',
        subtitle: 'Què portes ara i què pots agafar',
        yours: 'El portes tu',
        none: 'No portes cap vehicle',
        noneFree: 'I ara mateix no en queda cap de lliure. Pregunta a oficina.',
        free: 'Lliures ara',
        freeHint: 'Agafa\'n un i queda al teu nom fins que el deixis',
        assigned: 'Assignats a tu',
        assignedHint: 'Els que l\'oficina et té posats de manera fixa',
        myRefuels: 'Els meus repostatges',
      },
      actions: {
        new: 'Vehicle nou',
        refuel: 'Apuntar repostatge',
        release: 'Deixar',
        pickUp: 'Agafar',
        service: 'Apuntar revisió',
        archive: 'Arxivar',
        restore: 'Retornar a la flota',
        forceRelease: 'Deixar-lo igualment',
        void: 'Anul·lar',
        edit: 'Editar',
        ticket: 'Veure tiquet',
      },
      sections: { identity: 'El vehicle', out: 'És fora', refuels: 'Repostatges', tickets: 'Tiquets' },
      ticketsHint: 'Les fotos que es van fer al sortidor, per quadrar la targeta de combustible',
      noTickets: 'Encara no hi ha cap tiquet. Apareixen aquí tan bon punt algú apunti un repostatge amb foto.',
      ticketOf: 'Tiquet del {date}',
      moreTickets: 'Hi ha {count} tiquets més. S’obren des de la seva fila de la taula de sota.',
      form: {
        editTitle: 'Editar vehicle',
        backToSheet: 'La fitxa',
        changes: 'El que canvia',
        changesHint: 'El que es toca cada setmana. La resta és més avall.',
        identityHint: "S'escriu una vegada i no es torna a mirar.",
      },
      fields: {
        vehicle: 'Vehicle',
        name: 'Nom',
        plate: 'Matrícula',
        brand: 'Marca',
        model: 'Model',
        year: 'Any',
        fuelType: 'Combustible',
        status: 'Estat',
        statusHint: 'Un vehicle que algú porta no es pot enviar al taller des d\'aquí',
        km: 'Quilòmetres',
        kmHint: 'Només pugen. Un repostatge els actualitza sol',
        since: 'Agafat',
        who: 'Qui',
        date: 'Data',
        litres: 'Litres',
        totalCost: 'Import',
        station: 'Benzinera',
        photo: 'Foto del tiquet',
        notes: 'Notes',
        note: 'Nota',
        assignedTo: 'Assignat a',
        assignedToHint: 'L\'assignació fixa, diferent de qui el porta avui',
        nobody: 'Ningú',
      },
      statuses: { active: 'Actiu', maintenance: 'Al taller', inactive: 'De baixa' },
      states: { free: 'Lliure', out: 'Fora' },
      fuels: {
        diesel: 'Dièsel', petrol: 'Gasolina', electric: 'Elèctric',
        hybrid: 'Híbrid', lpg: 'GLP', other: 'Altre',
      },
      stats: {
        total: 'Vehicles',
        available: 'Lliures',
        out: 'Fora',
        maintenance: 'Al taller',
        consumption: 'Consum / 100 km',
        refuels: 'Repostatges',
        sinceService: 'Km des de la revisió',
      },
      filters: {
        search: 'Cercar',
        searchPlaceholder: 'Matrícula, nom, marca…',
        allStatuses: 'Tots',
        availability: 'Disponibilitat',
        allAvailability: 'Tots',
        show: 'Veure',
        inFleet: 'A la flota',
        archived: 'Arxivats',
      },
      refuel: {
        title: 'Apuntar un repostatge',
        kmHint: 'Els que marca el quadre ara mateix',
        costHint: 'El total del tiquet. El preu per litre surt sol',
        photoHint: 'Opcional, tret que l\'empresa l\'exigeixi',
        saved: 'Repostatge apuntat',
      },
      release: {
        title: 'Deixar el vehicle',
        kmHint: 'Si t\'hi has fixat. Si no, deixa-ho en blanc',
        done: 'Vehicle deixat',
      },
      service: {
        title: 'Apuntar una revisió',
        kmHint: 'Els que marcava en passar pel taller',
        saved: 'Revisió apuntada',
      },
      confirmArchive: {
        title: 'Arxivar el vehicle?',
        message: '«{name}» deixarà d\'aparèixer. Els seus repostatges i la feina que el nomena hi segueixen.',
      },
      confirmForce: {
        title: 'Deixar-lo igualment?',
        message: 'El té {name}. Deixa\'l només si saps que el vehicle ja ha tornat.',
      },
      confirmVoid: {
        title: 'Anul·lar el repostatge?',
        message: '{code} deixarà de comptar per al consum, però es queda a la llista ratllat.',
      },
      notifications: {
        categories: {
          serviceDue: 'Revisió d\'un vehicle',
          released: 'Et deixen sense vehicle',
        },
        released: {
          title: 'Ja no portes {vehicle}',
          body: '{who} ha retornat {plate} a la flota.',
        },
        serviceDue: {
          title: 'Hi ha vehicles per revisar',
          body: '{count} vehicle(s) han passat l\'interval. El primer, {first}.',
        },
      },
    },
    modules: { fleet: 'Flota' },
    permissions: {
      fleet: {
        read: 'Veure la flota i qui porta cada vehicle',
        drive: 'Agafar un vehicle, deixar-lo i apuntar repostatges',
        manage: 'Donar d\'alta vehicles i veure la despesa de tota la flota',
      },
    },
    audit: {
      actions: {
        vehicle: {
          created: 'Vehicle donat d\'alta',
          updated: 'Vehicle modificat',
          serviced: 'Revisió apuntada',
          archived: 'Vehicle arxivat',
          restored: 'Vehicle retornat a la flota',
          picked_up: 'Algú agafa un vehicle',
          released: 'Algú deixa un vehicle',
          force_released: 'S\'allibera un vehicle aliè',
        },
        fuel_log: { recorded: 'Repostatge apuntat', voided: 'Repostatge anul·lat' },
      },
    },
    assistant: {
      openers: {
        fleet: {
          'what-am-i-driving': 'Quin vehicle porto ara?',
          'log-refuel': 'Acabo de repostar',
          'any-free': 'Queda alguna furgoneta lliure?',
          'fuel-spend': 'Quant portem gastat en combustible?',
        },
      },
    },
    settings: {
      ns: {
        fleet: {
          title: 'Flota',
          subtitle: 'Com s\'agafen els vehicles i què s\'exigeix en repostar',
          oneVehiclePerDriver: {
            label: 'Una persona només pot portar un vehicle alhora',
            help: 'Encès, perquè és el que fa que «qui té la Kangoo» tingui una sola resposta.',
          },
          requireTicketPhoto: {
            label: 'Exigir la foto del tiquet a cada repostatge',
            help: 'Apagat de sèrie: un sortidor sense impressora o un mòbil sense bateria acaben en litres sense apuntar.',
          },
          serviceEveryKm: {
            label: 'Avisar quan un vehicle passi aquests quilòmetres des de la revisió',
            help: '0 apaga l\'avís. Es mesura des de l\'última revisió apuntada, no des de zero.',
          },
          serviceAlertRoles: {
            label: 'Qui s\'assabenta que toca revisió',
            help: 'Es comprova un cop cada nit.',
          },
        },
      },
    },
  },

  en: {
    errors: {
      ALREADY_DRIVING: 'You already have {code}. Hand it back before taking another.',
      ALREADY_VOID: 'That refuel does not exist or is already void.',
      NOT_YOUR_VEHICLE: 'You are not driving that vehicle, so you cannot hand it back.',
      PLATE_TAKEN: 'That plate is already on {code}.',
      VEHICLE_FREE: 'Nobody has that vehicle.',
      VEHICLE_IN_USE: 'That vehicle is out with somebody. It has to be handed back first.',
      VEHICLE_NOT_AVAILABLE: 'That vehicle is not available.',
      VEHICLE_TAKEN: 'Somebody else has that vehicle: {driver}.',
    },
    fleet: {
      // Los avisos de la mañana y de la tarjeta «Hoy» (backend/nudges.js).
      nudges: {
        serviceDue: '{count} vehicles due for a service',
        serviceDueOne: '{vehicle} is due for a service',
      },
      // La respuesta que contestan las reglas sin el modelo (queries.js).
      assistant: {
        query: {
          mine: {
            current: 'You are driving {vehicle}.',
            assigned: 'Assigned to you: {list}.',
            none: 'You have no vehicle; {free} are free.',
            noneFree: 'You have no vehicle and none is free.',
          },
        },
        fuel: {
          summary: {
            plain: 'Refuel of {litres} l in {vehicle}',
            paid: 'Refuel of {litres} l, €{total}, in {vehicle}',
          },
          action: 'Write down a refuel',
          field: {
            vehicle: 'Vehicle',
            litres: 'Litres',
            total: 'Total (€)',
            price: 'Price per litre',
            km: 'Odometer (km)',
            station: 'Station',
          },
          where: 'Vehicle',
          note: {
            noVehicle: 'Pick the vehicle: none is clear from what was said',
            noKm: 'The odometer reading is missing',
            belowKm: 'The odometer cannot go backwards: it was at {km} km',
            photo: 'This company requires the ticket photo: write it from My vehicle',
          },
          spoken: { plain: '{litres} litres. Shall I write it down?', paid: '{litres} litres, {total} euros. Shall I write it down?' },
          askLitres: 'How many litres?',
          askKm: 'What does the odometer say?',
          outcome: 'Refuel {code} written down',
        },
      },
      title: 'Fleet',
      dashboard: {
        available: 'Vehicles free',
        availableHelp: 'How many active vehicles nobody has taken right now.',
        out: 'Vehicles out',
        outHelp: 'How many vehicles somebody is holding at this moment.',
        inWorkshop: 'In the workshop',
        inWorkshopHelp: 'How many vehicles are marked as under maintenance and cannot be taken.',
        fuelMonth: 'Fuel this month',
        fuelMonthHelp: 'What refuelling has cost since the 1st of the current month, in euros.',
        latestRefuels: 'Latest refuels',
        latestRefuelsHelp: 'The most recent refuels, with the plate and the litres. Stretch the card and more fit.',
      },
      subtitle: 'The company vehicles, who is driving them and what they burn',
      detailTitle: 'Vehicle',
      newTitle: 'New vehicle',
      newHint: 'A name, a plate and the kilometres it reads today',
      empty: 'No vehicles yet',
      noRefuels: 'No refuels yet',
      saved: 'Vehicle saved',
      archived: 'Vehicle archived',
      restored: 'Vehicle brought back into the fleet',
      archivedBanner: 'This vehicle is archived: it is on no list and nobody can take it.',
      tookIt: 'You took {name}',
      outWith: '{name} has it right now.',
      consumptionHint: 'Consumption appears from the second refuel: it needs a distance between two of them to be worked out.',
      voidedFromScreen: 'Voided from the vehicle file',
      myVehicle: {
        title: 'My vehicle',
        subtitle: 'What you are driving and what you could take',
        yours: 'Yours right now',
        none: 'You are not driving anything',
        noneFree: 'And there is nothing free at the moment. Ask the office.',
        free: 'Free now',
        freeHint: 'Take one and it stays in your name until you hand it back',
        assigned: 'Assigned to you',
        assignedHint: 'The ones the office has permanently allocated to you',
        myRefuels: 'My refuels',
      },
      actions: {
        new: 'New vehicle',
        refuel: 'Log a refuel',
        release: 'Hand back',
        pickUp: 'Take',
        service: 'Log a service',
        archive: 'Archive',
        restore: 'Bring back',
        forceRelease: 'Release it anyway',
        void: 'Void',
        edit: 'Edit',
        ticket: 'See ticket',
      },
      sections: { identity: 'The vehicle', out: 'It is out', refuels: 'Refuels', tickets: 'Tickets' },
      ticketsHint: 'The photos taken at the pump, for reconciling the fuel card',
      noTickets: 'No tickets yet. They show up here as soon as somebody logs a refuel with a photo.',
      ticketOf: 'Ticket of {date}',
      moreTickets: 'There are {count} more tickets. They open from their row in the table below.',
      form: {
        editTitle: 'Edit vehicle',
        backToSheet: 'The sheet',
        changes: 'What changes',
        changesHint: 'What gets touched every week. The rest is further down.',
        identityHint: 'Written once and never looked at again.',
      },
      fields: {
        vehicle: 'Vehicle',
        name: 'Name',
        plate: 'Plate',
        brand: 'Make',
        model: 'Model',
        year: 'Year',
        fuelType: 'Fuel',
        status: 'Status',
        statusHint: 'A vehicle somebody is driving cannot be sent to the garage from here',
        km: 'Kilometres',
        kmHint: 'They only go up. A refuel updates them on its own',
        since: 'Taken',
        who: 'Who',
        date: 'Date',
        litres: 'Litres',
        totalCost: 'Total',
        station: 'Station',
        photo: 'Ticket photo',
        notes: 'Notes',
        note: 'Note',
        assignedTo: 'Assigned to',
        assignedToHint: 'The permanent allocation, different from who has it today',
        nobody: 'Nobody',
      },
      statuses: { active: 'Active', maintenance: 'In the garage', inactive: 'Off the road' },
      states: { free: 'Free', out: 'Out' },
      fuels: {
        diesel: 'Diesel', petrol: 'Petrol', electric: 'Electric',
        hybrid: 'Hybrid', lpg: 'LPG', other: 'Other',
      },
      stats: {
        total: 'Vehicles',
        available: 'Free',
        out: 'Out',
        maintenance: 'In the garage',
        consumption: 'Consumption / 100 km',
        refuels: 'Refuels',
        sinceService: 'Km since service',
      },
      filters: {
        search: 'Search',
        searchPlaceholder: 'Plate, name, make…',
        allStatuses: 'All',
        availability: 'Availability',
        allAvailability: 'All',
        show: 'Show',
        inFleet: 'In the fleet',
        archived: 'Archived',
      },
      refuel: {
        title: 'Log a refuel',
        kmHint: 'What the dashboard reads right now',
        costHint: 'The total on the receipt. The price per litre is worked out',
        photoHint: 'Optional, unless the company requires it',
        saved: 'Refuel logged',
      },
      release: {
        title: 'Hand the vehicle back',
        kmHint: 'If you looked. If not, leave it blank',
        done: 'Vehicle handed back',
      },
      service: {
        title: 'Log a service',
        kmHint: 'What it read when it went in',
        saved: 'Service logged',
      },
      confirmArchive: {
        title: 'Archive the vehicle?',
        message: '“{name}” stops appearing. Its refuels and the work that names it stay.',
      },
      confirmForce: {
        title: 'Release it anyway?',
        message: '{name} has it. Only release it if you know the vehicle is back.',
      },
      confirmVoid: {
        title: 'Void the refuel?',
        message: '{code} stops counting towards consumption, but stays in the list struck through.',
      },
      notifications: {
        categories: {
          serviceDue: 'Vehicle due for a service',
          released: 'A vehicle of yours was released',
        },
        released: {
          title: 'You are no longer driving {vehicle}',
          body: '{who} put {plate} back in the fleet.',
        },
        serviceDue: {
          title: 'Vehicles are due for a service',
          body: '{count} vehicle(s) past the interval. The first is {first}.',
        },
      },
    },
    modules: { fleet: 'Fleet' },
    permissions: {
      fleet: {
        read: 'See the fleet and who is driving what',
        drive: 'Take a vehicle, hand it back and log refuels',
        manage: 'Add vehicles and see what the whole fleet spends',
      },
    },
    audit: {
      actions: {
        vehicle: {
          created: 'Vehicle added',
          updated: 'Vehicle changed',
          serviced: 'Service logged',
          archived: 'Vehicle archived',
          restored: 'Vehicle brought back into the fleet',
          picked_up: 'Somebody took a vehicle',
          released: 'Somebody handed a vehicle back',
          force_released: 'Somebody else\'s vehicle was released',
        },
        fuel_log: { recorded: 'Refuel logged', voided: 'Refuel voided' },
      },
    },
    assistant: {
      openers: {
        fleet: {
          'what-am-i-driving': 'What am I driving right now?',
          'log-refuel': 'I have just refuelled',
          'any-free': 'Is there a van free?',
          'fuel-spend': 'How much have we spent on fuel?',
        },
      },
    },
    settings: {
      ns: {
        fleet: {
          title: 'Fleet',
          subtitle: 'How vehicles are taken, and what a refuel has to carry',
          oneVehiclePerDriver: {
            label: 'A driver can only have one vehicle at a time',
            help: 'On, because it is what makes “who has the Kangoo” have one answer.',
          },
          requireTicketPhoto: {
            label: 'Require the ticket photo on every refuel',
            help: 'Off by default: a pump with no printer or a phone with no battery end up as litres nobody logged.',
          },
          serviceEveryKm: {
            label: 'Warn when a vehicle passes this many kilometres since its service',
            help: '0 turns the warning off. It measures from the last service somebody logged, not from zero.',
          },
          serviceAlertRoles: {
            label: 'Who hears that a service is due',
            help: 'Checked once a night.',
          },
        },
      },
    },
  },

  fr: {
    errors: {
      ALREADY_DRIVING: 'Vous avez déjà {code}. Rendez-le avant d’en prendre un autre.',
      ALREADY_VOID: 'Ce plein n’existe pas ou est déjà annulé.',
      NOT_YOUR_VEHICLE: 'Ce n’est pas vous qui avez ce véhicule : vous ne pouvez pas le rendre.',
      PLATE_TAKEN: 'Cette immatriculation est déjà sur {code}.',
      VEHICLE_FREE: 'Personne n’a ce véhicule.',
      VEHICLE_IN_USE: 'Ce véhicule est sorti avec quelqu’un. Il doit d’abord être rendu.',
      VEHICLE_NOT_AVAILABLE: 'Ce véhicule n’est pas disponible.',
      VEHICLE_TAKEN: 'Quelqu’un d’autre a ce véhicule : {driver}.',
    },
    fleet: {
      // Los avisos de la mañana y de la tarjeta «Hoy» (backend/nudges.js).
      nudges: {
        serviceDue: '{count} véhicules à réviser',
        serviceDueOne: 'révision de {vehicle}',
      },
      // La respuesta que contestan las reglas sin el modelo (queries.js).
      assistant: {
        query: {
          mine: {
            current: 'Vous conduisez {vehicle}.',
            assigned: 'Attribué à vous : {list}.',
            none: 'Vous n’avez aucun véhicule ; {free} sont libres.',
            noneFree: 'Vous n’avez aucun véhicule et aucun n’est libre.',
          },
        },
        fuel: {
          summary: {
            plain: 'Plein de {litres} l pour {vehicle}',
            paid: 'Plein de {litres} l, {total} €, pour {vehicle}',
          },
          action: 'Noter un plein',
          field: {
            vehicle: 'Véhicule',
            litres: 'Litres',
            total: 'Montant (€)',
            price: 'Prix au litre',
            km: 'Compteur (km)',
            station: 'Station',
          },
          where: 'Véhicule',
          note: {
            noVehicle: 'Choisissez le véhicule : rien de clair dans ce qui a été dit',
            noKm: 'Il manque le relevé du compteur',
            belowKm: 'Le compteur ne recule pas : il était à {km} km',
            photo: 'Cette entreprise exige la photo du ticket : notez-le depuis Mon véhicule',
          },
          spoken: { plain: '{litres} litres. Je le note ?', paid: '{litres} litres, {total} euros. Je le note ?' },
          askLitres: 'Combien de litres ?',
          askKm: 'Que dit le compteur ?',
          outcome: 'Plein {code} noté',
        },
      },
      title: 'Flotte',
      dashboard: {
        available: 'Véhicules libres',
        availableHelp: 'Combien de véhicules actifs ne sont pris par personne en ce moment.',
        out: 'Véhicules sortis',
        outHelp: 'Combien de véhicules sont pris par quelqu\'un en ce moment.',
        inWorkshop: 'À l\'atelier',
        inWorkshopHelp: 'Combien de véhicules sont marqués en maintenance et ne peuvent pas être pris.',
        fuelMonth: 'Carburant du mois',
        fuelMonthHelp: 'Ce qu\'ont coûté les pleins depuis le 1er du mois en cours, en euros.',
        latestRefuels: 'Derniers pleins',
        latestRefuelsHelp: 'Les pleins les plus récents, avec la plaque et les litres. Étirez la carte et il en tient plus.',
      },
      subtitle: 'Les véhicules de l\'entreprise, qui les conduit et ce qu\'ils consomment',
      detailTitle: 'Véhicule',
      newTitle: 'Nouveau véhicule',
      newHint: 'Un nom, une plaque et le kilométrage affiché aujourd\'hui',
      empty: 'Aucun véhicule pour le moment',
      noRefuels: 'Aucun plein pour le moment',
      saved: 'Véhicule enregistré',
      archived: 'Véhicule archivé',
      restored: 'Véhicule remis dans la flotte',
      archivedBanner: 'Ce véhicule est archivé : il ne figure sur aucune liste et personne ne peut le prendre.',
      tookIt: 'Vous avez pris {name}',
      outWith: '{name} l\'a en ce moment.',
      consumptionHint: 'La consommation apparaît à partir du deuxième plein : il faut une distance entre deux pour la calculer.',
      voidedFromScreen: 'Annulé depuis la fiche du véhicule',
      myVehicle: {
        title: 'Mon véhicule',
        subtitle: 'Ce que vous conduisez et ce que vous pouvez prendre',
        yours: 'Vous l\'avez',
        none: 'Vous ne conduisez aucun véhicule',
        noneFree: 'Et il n\'y en a aucun de libre en ce moment. Demandez au bureau.',
        free: 'Libres maintenant',
        freeHint: 'Prenez-en un et il reste à votre nom jusqu\'à ce que vous le rendiez',
        assigned: 'Qui vous sont attribués',
        assignedHint: 'Ceux que le bureau vous a attribués de façon permanente',
        myRefuels: 'Mes pleins',
      },
      actions: {
        new: 'Nouveau véhicule',
        refuel: 'Noter un plein',
        release: 'Rendre',
        pickUp: 'Prendre',
        service: 'Noter une révision',
        archive: 'Archiver',
        restore: 'Remettre en flotte',
        forceRelease: 'Le libérer quand même',
        void: 'Annuler',
        edit: 'Modifier',
        ticket: 'Voir le ticket',
      },
      sections: { identity: 'Le véhicule', out: 'Il est sorti', refuels: 'Pleins', tickets: 'Tickets' },
      ticketsHint: 'Les photos prises à la pompe, pour rapprocher la carte carburant',
      noTickets: "Pas encore de ticket. Ils apparaissent ici dès que quelqu'un saisit un plein avec une photo.",
      ticketOf: 'Ticket du {date}',
      moreTickets: 'Il y a {count} tickets de plus. Ils s’ouvrent depuis leur ligne du tableau ci-dessous.',
      form: {
        editTitle: 'Modifier le véhicule',
        backToSheet: 'La fiche',
        changes: 'Ce qui change',
        changesHint: 'Ce que l\'on touche chaque semaine. Le reste est plus bas.',
        identityHint: "Écrit une fois et jamais relu.",
      },
      fields: {
        vehicle: 'Véhicule',
        name: 'Nom',
        plate: 'Plaque',
        brand: 'Marque',
        model: 'Modèle',
        year: 'Année',
        fuelType: 'Carburant',
        status: 'État',
        statusHint: 'Un véhicule que quelqu\'un conduit ne peut pas être envoyé au garage d\'ici',
        km: 'Kilomètres',
        kmHint: 'Ils ne font que monter. Un plein les met à jour tout seul',
        since: 'Pris',
        who: 'Qui',
        date: 'Date',
        litres: 'Litres',
        totalCost: 'Montant',
        station: 'Station',
        photo: 'Photo du ticket',
        notes: 'Notes',
        note: 'Note',
        assignedTo: 'Attribué à',
        assignedToHint: 'L\'attribution permanente, différente de qui l\'a aujourd\'hui',
        nobody: 'Personne',
      },
      statuses: { active: 'Actif', maintenance: 'Au garage', inactive: 'Hors service' },
      states: { free: 'Libre', out: 'Sorti' },
      fuels: {
        diesel: 'Diesel', petrol: 'Essence', electric: 'Électrique',
        hybrid: 'Hybride', lpg: 'GPL', other: 'Autre',
      },
      stats: {
        total: 'Véhicules',
        available: 'Libres',
        out: 'Sortis',
        maintenance: 'Au garage',
        consumption: 'Consommation / 100 km',
        refuels: 'Pleins',
        sinceService: 'Km depuis la révision',
      },
      filters: {
        search: 'Rechercher',
        searchPlaceholder: 'Plaque, nom, marque…',
        allStatuses: 'Tous',
        availability: 'Disponibilité',
        allAvailability: 'Tous',
        show: 'Afficher',
        inFleet: 'Dans la flotte',
        archived: 'Archivés',
      },
      refuel: {
        title: 'Noter un plein',
        kmHint: 'Ce qu\'affiche le tableau de bord maintenant',
        costHint: 'Le total du ticket. Le prix au litre se calcule tout seul',
        photoHint: 'Facultatif, sauf si l\'entreprise l\'exige',
        saved: 'Plein noté',
      },
      release: {
        title: 'Rendre le véhicule',
        kmHint: 'Si vous avez regardé. Sinon, laissez vide',
        done: 'Véhicule rendu',
      },
      service: {
        title: 'Noter une révision',
        kmHint: 'Ce qu\'il affichait en entrant au garage',
        saved: 'Révision notée',
      },
      confirmArchive: {
        title: 'Archiver le véhicule ?',
        message: '« {name} » n\'apparaîtra plus. Ses pleins et le travail qui le nomme restent.',
      },
      confirmForce: {
        title: 'Le libérer quand même ?',
        message: '{name} l\'a. Ne le libérez que si vous savez que le véhicule est rentré.',
      },
      confirmVoid: {
        title: 'Annuler le plein ?',
        message: '{code} ne comptera plus dans la consommation, mais reste dans la liste barré.',
      },
      notifications: {
        categories: {
          serviceDue: 'Révision d\'un véhicule',
          released: 'On vous retire un véhicule',
        },
        released: {
          title: 'Vous ne conduisez plus {vehicle}',
          body: '{who} a remis {plate} dans la flotte.',
        },
        serviceDue: {
          title: 'Des véhicules sont à réviser',
          body: '{count} véhicule(s) ont dépassé l\'intervalle. Le premier est {first}.',
        },
      },
    },
    modules: { fleet: 'Flotte' },
    permissions: {
      fleet: {
        read: 'Voir la flotte et qui conduit quoi',
        drive: 'Prendre un véhicule, le rendre et noter des pleins',
        manage: 'Créer des véhicules et voir la dépense de toute la flotte',
      },
    },
    audit: {
      actions: {
        vehicle: {
          created: 'Véhicule créé',
          updated: 'Véhicule modifié',
          serviced: 'Révision notée',
          archived: 'Véhicule archivé',
          restored: 'Véhicule remis dans la flotte',
          picked_up: 'Quelqu\'un prend un véhicule',
          released: 'Quelqu\'un rend un véhicule',
          force_released: 'Un véhicule d\'autrui est libéré',
        },
        fuel_log: { recorded: 'Plein noté', voided: 'Plein annulé' },
      },
    },
    assistant: {
      openers: {
        fleet: {
          'what-am-i-driving': 'Quel véhicule je conduis en ce moment ?',
          'log-refuel': 'Je viens de faire le plein',
          'any-free': 'Y a-t-il une camionnette libre ?',
          'fuel-spend': 'Combien avons-nous dépensé en carburant ?',
        },
      },
    },
    settings: {
      ns: {
        fleet: {
          title: 'Flotte',
          subtitle: 'Comment les véhicules se prennent, et ce qu\'un plein doit porter',
          oneVehiclePerDriver: {
            label: 'Une personne ne peut avoir qu\'un véhicule à la fois',
            help: 'Activé, parce que c\'est ce qui fait que « qui a la Kangoo » n\'a qu\'une réponse.',
          },
          requireTicketPhoto: {
            label: 'Exiger la photo du ticket à chaque plein',
            help: 'Désactivé par défaut : une pompe sans imprimante ou un téléphone sans batterie finissent en litres non notés.',
          },
          serviceEveryKm: {
            label: 'Alerter quand un véhicule dépasse ce kilométrage depuis sa révision',
            help: '0 désactive l\'alerte. On mesure depuis la dernière révision notée, pas depuis zéro.',
          },
          serviceAlertRoles: {
            label: 'Qui est prévenu qu\'une révision est due',
            help: 'Vérifié une fois par nuit.',
          },
        },
      },
    },
  },
}

// El vocabulario de analítica del módulo, aparte porque es un contrato con el
// catálogo de `backend/analytics.js` y no copia de una pantalla. Ver ese fichero.
import { withAnalytics } from './i18n/analytics.js'

// What the assistant panel says while this module's tools run: another
// contract with the backend (backend/assistant.js), in its own file.
import { withToolLabels } from './i18n/toolLabels.js'
// The reasons and field codes its errors carry (frontend/i18n/errorReasons.js).
import { withErrorReasons } from './i18n/errorReasons.js'

export const messages = withErrorReasons(withToolLabels(withAnalytics(baseMessages, 'fleet')))

export default { routes, navigation, messages }
